import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadCloud, Link2, FolderOpen, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ACCEPT, kindOf, validateFile, uploadCourseMedia, parseVideoLink } from '../../lib/mediaUpload';
import { formatApiErrorDetail } from '../../lib/apiError';

const FileRow = ({ item }) => (
  <div className="flex items-center gap-3 rounded-lg bg-zinc-800/60 px-3 py-2" data-testid="media-upload-row">
    <div className="flex-1 min-w-0">
      <p className="text-sm text-zinc-200 truncate">{item.name}</p>
      <div className="h-1.5 mt-1.5 rounded-full bg-zinc-700 overflow-hidden">
        <div className={`h-full transition-[width] duration-300 ${item.error ? 'bg-rose-500' : 'bg-teal-400'}`} style={{ width: `${item.error ? 100 : item.progress}%` }} />
      </div>
      {item.error && <p className="text-xs text-rose-400 mt-1">{item.error}</p>}
    </div>
    {item.error ? <AlertCircle className="h-4 w-4 text-rose-400" /> : item.done ? <CheckCircle2 className="h-4 w-4 text-teal-400" /> : <span className="text-xs text-zinc-400 tabular-nums w-9 text-right">{item.progress}%</span>}
  </div>
);

export default function SectionMediaUploader({ open, onClose, onAdd, token, courseId, courseTitle, renderLibrary }) {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [link, setLink] = useState('');
  const patch = (key, p) => setItems((prev) => prev.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const handleFiles = async (files) => {
    for (const file of Array.from(files || [])) {
      const key = `${file.name}_${Date.now()}_${Math.random()}`;
      const error = validateFile(file);
      setItems((prev) => [...prev, { key, name: file.name, progress: 0, error }]);
      if (error) continue;
      try {
        const res = await uploadCourseMedia(file, { token, courseId, courseTitle, onProgress: (progress) => patch(key, { progress }) });
        onAdd({ url: res.url, type: kindOf(file), filename: file.name, size: file.size, content_type: file.type, media_id: res.id });
        patch(key, { progress: 100, done: true });
      } catch (e) {
        patch(key, { error: formatApiErrorDetail(e.response?.data?.detail, 'Upload failed') });
      }
    }
  };

  const addLink = () => {
    const parsed = parseVideoLink(link);
    if (!parsed) return toast.error('Paste a YouTube or Vimeo link');
    onAdd({ type: 'embed', url: link.trim(), embed_url: parsed.embed_url, provider: parsed.provider, thumbnail_url: parsed.thumb, filename: `${parsed.provider} video` });
    setLink('');
  };

  const close = () => { setItems([]); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl" data-testid="section-media-uploader">
        <DialogHeader>
          <DialogTitle>Add media to this section</DialogTitle>
          <DialogDescription className="text-zinc-400">Uploads are added right away and also saved to the Media Centre for reuse.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="upload">
          <TabsList className="bg-zinc-800">
            <TabsTrigger value="upload" data-testid="media-tab-upload"><UploadCloud className="h-4 w-4 mr-1.5" />Upload</TabsTrigger>
            <TabsTrigger value="link" data-testid="media-tab-link"><Link2 className="h-4 w-4 mr-1.5" />YouTube / Vimeo</TabsTrigger>
            <TabsTrigger value="library" data-testid="media-tab-library"><FolderOpen className="h-4 w-4 mr-1.5" />Media Centre</TabsTrigger>
          </TabsList>
          <TabsContent value="upload" className="space-y-3">
            <button type="button" onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
              className={`w-full rounded-xl border-2 border-dashed py-10 text-center transition-colors ${dragging ? 'border-teal-400 bg-teal-400/5' : 'border-zinc-700 hover:border-zinc-500'}`}
              data-testid="media-dropzone">
              <UploadCloud className="h-9 w-9 mx-auto text-teal-400 mb-2" />
              <p className="text-sm text-zinc-200">Drag files here or <span className="text-teal-300 underline">browse</span></p>
              <p className="text-xs text-zinc-500 mt-1">Images and infographics up to 10 MB · PDF, Word, PowerPoint up to 25 MB · Videos up to 500 MB</p>
            </button>
            <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="media-file-input" />
            <div className="space-y-2 max-h-52 overflow-y-auto">{items.map((it) => <FileRow key={it.key} item={it} />)}</div>
          </TabsContent>
          <TabsContent value="link" className="space-y-3">
            <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/…" className="bg-zinc-800 border-zinc-700" data-testid="media-link-input" onKeyDown={(e) => e.key === 'Enter' && addLink()} />
            <Button onClick={addLink} className="bg-teal-500 hover:bg-teal-400 text-black" data-testid="media-link-add-btn">Add video</Button>
          </TabsContent>
          <TabsContent value="library" className="max-h-[50vh] overflow-y-auto">
            {renderLibrary((m) => { onAdd({ url: m.url, type: m.type, filename: m.original_filename || m.filename, size: m.size, content_type: m.content_type, media_id: m.id || m.media_id }); toast.success('Added from Media Centre'); })}
          </TabsContent>
        </Tabs>
        {items.some((i) => !i.done && !i.error) && <p className="text-xs text-zinc-400 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Uploading… keep this window open</p>}
      </DialogContent>
    </Dialog>
  );
}
